import { useEffect, useMemo, useState } from 'react';
import { API } from '../service/ProductsService';
import { basket } from '../utils/basketStore';

// "טעינת רשימה" ברשימת הקניות: מדביקים רשימה מוכנה (וואטסאפ, פתקים), והמערכת מוצאת מוצר לכל שורה (spMatchShoppingList).
// שלבים: הדבקה → לכל שורה כללית ("חלב") שאלה "לאיזה חלב התכוונת?" עם תמונות → סיכום → הוספת הכל.
// שורה עם התאמה אחת נבחרת לבד; שורה בלי התאמה - "לא נמצא".

const MAX_LINES = 50;   // כמו @maxLines ב-spMatchShoppingList

// פירוק שורה לטקסט + כמות: "2 חלב", "חלב x2", "חלב ×2", "2x חלב", "חלב - 2", "חלב 2"; בלי כמות = 1.
// מסיר תבליטים ומספור ("-", "•", "*", "1.", "1)").
// מספר שאחריו יחידת מידה ("500 גרם גבינה") הוא חלק מהשם, לא כמות
const UNIT_AFTER = /^(?:גרם|גר|ג'|מ"ל|מל|ליטר|ל'|ק"ג|קג|יח'|%)/;

export const parseLine = (raw) => {
  const text = raw.replace(/^\s*(?:[-•*·▪]|\d+[.)])\s*/, '').trim();
  const toQty = (n) => (Number(n) > 0 ? Number(n) : null);   // כמות = מספר שלם 1–99 ("קולה 1.5" - נפח, לא כמות)

  // מספר בהתחלה: "2 חלב", "2x חלב"
  let m = text.match(/^(\d{1,2})\s*[x×X]?\s+(.{2,})$/);
  if (m && toQty(m[1]) && !UNIT_AFTER.test(m[2])) return { text: m[2].trim(), qty: toQty(m[1]) };

  // מספר בסוף: "חלב x2", "חלב - 2", "חלב 2" (לא "חלב 3%" - אין רווח/סימן לפני ה-%)
  m = text.match(/^(.{2,}?)\s*(?:[x×X]|\s-|\s–)\s*(\d{1,2})$/) || text.match(/^(.{2,}?[^\d.,])\s+(\d{1,2})$/);
  if (m && toQty(m[2])) return { text: m[1].trim(), qty: toQty(m[2]) };

  return { text, qty: 1 };
};

const splitLines = (value) =>
  value.split(/[\n,;]+/).map((l) => l.trim()).filter((l) => l.length > 0).map(parseLine).filter((l) => l.text.length >= 2);

const formatPrice = (n) => `₪${Number(n || 0).toFixed(2)}`;
const imageSrc = (url) => url ? url.replace('{{size}}', 'medium').replace(/\{\{extension[^}]*\}\}/, 'jpg') : null;
const productMeta = (p) => [
  p.brandName,
  p.isWeighable ? 'שקיל' : (p.weight && p.unitName ? `${Number(p.weight)} ${p.unitName}` : null),
].filter(Boolean).join(' · ');

const s = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', zIndex: 120, overflowY: 'auto' },
  dialog:    { width: '100%', maxWidth: 760, background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 16px 48px rgba(0,0,0,.25)' },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 },
  title:     { margin: 0, fontSize: 20, fontWeight: 700 },
  close:     { background: 'none', border: 'none', fontSize: 26, lineHeight: 1, color: '#59636e' },
  text:      { margin: '0 0 10px', color: '#59636e', fontSize: 14, lineHeight: 1.6 },
  textarea:  { width: '100%', minHeight: 200, padding: 12, fontSize: 16, border: '1px solid #d0d7de', borderRadius: 10, resize: 'vertical', lineHeight: 1.6 },
  counter:   { fontSize: 13, color: '#59636e', margin: '6px 0 12px' },
  primary:   { padding: '11px 22px', fontSize: 16, fontWeight: 700, border: 'none', borderRadius: 10, background: '#1a3f9c', color: '#fff' },
  secondary: { padding: '10px 16px', fontSize: 14, fontWeight: 600, border: '1px solid #d0d7de', borderRadius: 10, background: '#fff', color: '#1f2328' },
  footer:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 16 },
  progress:  { fontSize: 13, color: '#59636e', marginBottom: 6 },
  question:  { margin: '0 0 12px', fontSize: 18, fontWeight: 700 },
  options:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 },
  option:    { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6, padding: 10, border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', color: 'inherit', font: 'inherit', cursor: 'pointer' },
  optionOn:  { borderColor: '#1a3f9c', boxShadow: '0 0 0 2px #1a3f9c inset', background: '#eef4ff' },
  imgBox:    { width: 88, height: 88, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderRadius: 8, overflow: 'hidden' },
  img:       { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  noImg:     { fontSize: 11, color: '#59636e' },
  optName:   { fontWeight: 600, fontSize: 14, lineHeight: 1.35 },
  optMeta:   { fontSize: 12, color: '#59636e' },
  optPrice:  { fontSize: 13, fontWeight: 700 },
  summary:   { listStyle: 'none', margin: 0, padding: 0 },
  sumItem:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f3f3' },
  sumLine:   { fontSize: 12, color: '#59636e' },
  sumName:   { fontWeight: 600 },
  sumMissing:{ color: '#9a6700' },
  change:    { background: 'none', border: 'none', color: '#1a3f9c', fontSize: 13, fontWeight: 600, padding: 0 },
  error:     { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10, marginBottom: 12 },
};

// כרטיס אפשרות עם תמונה
const OptionCard = ({ product, selected, onPick }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const src = imageSrc(product.imageUrl);
  return (
    <button type="button" style={{ ...s.option, ...(selected ? s.optionOn : null) }} onClick={onPick} aria-pressed={selected}>
      <span style={s.imgBox}>
        {src && !imgFailed
          ? <img style={s.img} src={src} alt="" loading="lazy" onError={() => setImgFailed(true)} />
          : <span style={s.noImg}>אין תמונה</span>}
      </span>
      <span style={s.optName}>{product.name}</span>
      {productMeta(product) && <span style={s.optMeta}>{productMeta(product)}</span>}
      <span style={s.optPrice}>
        מ-{formatPrice(product.minPrice)}{product.isWeighable ? ' לק"ג' : ''}
        {product.storeCount > 1 ? ` · ב-${product.storeCount} חנויות` : ''}
      </span>
    </button>
  );
};

const ImportListDialog = ({ onClose }) => {
  const [step, setStep] = useState('input');     // input → choose → summary
  const [value, setValue] = useState('');
  const [lines, setLines] = useState([]);        // [{ text, qty, options: [...], chosen: product | null }]
  const [questionIdx, setQuestionIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const parsed = useMemo(() => splitLines(value), [value]);
  // השורות שבהן יש כמה אפשרויות - שואלים עליהן אחת אחת
  const questions = useMemo(() => lines.map((l, i) => (l.options.length > 1 ? i : -1)).filter((i) => i >= 0), [lines]);

  const match = async () => {
    if (!parsed.length) { setError('נא להדביק רשימה - כל מוצר בשורה'); return; }
    if (parsed.length > MAX_LINES) { setError(`אפשר לטעון עד ${MAX_LINES} מוצרים בפעם אחת`); return; }
    setLoading(true);
    setError(null);
    try {
      const resp = await API.matchShoppingList(parsed.map((l) => l.text));
      const status = resp?.[0]?.[0];
      if (status?.code !== 0) { if (status?.code !== 90) setError(status?.msg || 'שגיאה בטעינת הרשימה'); return; }
      const byLine = {};
      (resp[1] || []).forEach((r) => { (byLine[r.lineIndex] = byLine[r.lineIndex] || []).push(r); });
      const next = parsed.map((l, i) => {
        const options = (byLine[i] || []).sort((a, b) => a.matchRank - b.matchRank);
        return { ...l, options, chosen: options.length === 1 ? options[0] : null };
      });
      setLines(next);
      const firstQuestion = next.findIndex((l) => l.options.length > 1);
      if (firstQuestion >= 0) { setQuestionIdx(0); setStep('choose'); } else setStep('summary');
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הרשימה');
    } finally {
      setLoading(false);
    }
  };

  const choose = (lineIndex, product) => {
    setLines((prev) => prev.map((l, i) => (i === lineIndex ? { ...l, chosen: product } : l)));
    if (questionIdx + 1 < questions.length) setQuestionIdx(questionIdx + 1);
    else setStep('summary');
  };

  const skip = () => choose(questions[questionIdx], null);

  const back = () => {
    if (questionIdx > 0) setQuestionIdx(questionIdx - 1);
    else setStep('input');
  };

  // מהסיכום - חזרה לשאלה של שורה מסוימת כדי לשנות את הבחירה
  const reask = (lineIndex) => {
    const q = questions.indexOf(lineIndex);
    if (q >= 0) { setQuestionIdx(q); setStep('choose'); }
  };

  const toAdd = lines.filter((l) => l.chosen);

  const addAll = () => {
    basket.addMany(toAdd.map((l) => ({ item: l.chosen, qty: l.qty })));
    onClose();
  };

  const currentLineIndex = questions[questionIdx];
  const current = step === 'choose' ? lines[currentLineIndex] : null;

  return (
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.dialog} role="dialog" aria-modal="true" aria-labelledby="import-title">
        <div style={s.header}>
          <h2 id="import-title" style={s.title}>טעינת רשימה</h2>
          <button type="button" style={s.close} onClick={onClose} aria-label="סגירה">×</button>
        </div>

        {error && <div style={s.error} role="alert">{error}</div>}

        {step === 'input' && (
          <>
            <p style={s.text}>
              הדביקו רשימה מוכנה - מוואטסאפ, מהפתקים או מכל מקום. כל מוצר בשורה (או מופרד בפסיק).
              אפשר לכתוב כמות: "2 חלב", "במבה x3". אחר כך נשאל על כל מוצר כללי לאיזה סוג התכוונתם.
            </p>
            <textarea
              style={s.textarea}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={'חלב\nלחם אחיד\n2 במבה\nביצים\nחלה x2'}
              aria-label="הרשימה - מוצר בכל שורה"
              autoFocus
            />
            <div style={s.counter}>{parsed.length ? `${parsed.length} מוצרים` : ''}</div>
            <div style={s.footer}>
              <span />
              <button type="button" style={s.primary} onClick={match} disabled={loading || !parsed.length}>
                {loading ? 'מחפש מוצרים…' : 'המשך'}
              </button>
            </div>
          </>
        )}

        {step === 'choose' && current && (
          <>
            <div style={s.progress}>שאלה {questionIdx + 1} מתוך {questions.length}</div>
            <h3 style={s.question}>
              לאיזה "{current.text}" התכוונת?{current.qty !== 1 ? ` (כמות: ${current.qty})` : ''}
            </h3>
            <div style={s.options}>
              {current.options.map((p) => (
                <OptionCard key={p.id} product={p} selected={current.chosen?.id === p.id} onPick={() => choose(currentLineIndex, p)} />
              ))}
            </div>
            <div style={s.footer}>
              <button type="button" style={s.secondary} onClick={back}>→ חזרה</button>
              <button type="button" style={s.secondary} onClick={skip}>דלג על המוצר הזה</button>
            </div>
          </>
        )}

        {step === 'summary' && (
          <>
            <p style={s.text}>
              {toAdd.length} מתוך {lines.length} מוצרים יתווספו לרשימה.
              {lines.length > toAdd.length ? ' מוצרים שלא נמצאו אפשר להוסיף בחיפוש.' : ''}
            </p>
            <ul style={s.summary}>
              {lines.map((l, i) => (
                <li key={i} style={s.sumItem}>
                  <div>
                    <div style={s.sumLine}>{l.text}{l.qty !== 1 ? ` · ${l.qty}` : ''}</div>
                    {l.chosen
                      ? <div style={s.sumName}>{l.chosen.name}</div>
                      : <div style={s.sumMissing}>{l.options.length ? 'דילגת' : 'לא נמצא'}</div>}
                  </div>
                  {l.options.length > 1 && (
                    <button type="button" style={s.change} onClick={() => reask(i)}>שינוי</button>
                  )}
                </li>
              ))}
            </ul>
            <div style={s.footer}>
              <button type="button" style={s.secondary} onClick={() => setStep('input')}>→ עריכת הרשימה</button>
              <button type="button" style={s.primary} onClick={addAll} disabled={!toAdd.length}>
                הוספת {toAdd.length} מוצרים לרשימה
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportListDialog;
